import React, { ChangeEvent, ReactNode, useState, useEffect } from 'react';

interface Suggestion {
  value: string;
  extraInfo: string;
  // Add any other fields you need for suggestions
}

interface SearchInputProps {
  placeholder: string;
  value: string;
  searchIcon?: ReactNode | string;
  size?: 'small' | 'medium' | 'large';
  onChange: (value: string) => void;
  suggestions?: Suggestion[]; // Change suggestions to array of objects
  onSuggestionSelect?: (suggestion: Suggestion) => void; // Update the callback type
  autoSuggestCallback?: (searchTerm: string) => void;
  onEnter?: () => void;
  onClearCallback?: () => void;
  extraInfo?: boolean;
}

const SearchInput: React.FC<SearchInputProps> = ({
  placeholder,
  value,
  searchIcon,
  onChange,
  size = 'medium',
  suggestions,
  extraInfo,
  onSuggestionSelect,
  autoSuggestCallback,
  onEnter,
  onClearCallback,
}) => {
  const [filteredSuggestions, setFilteredSuggestions] = useState<Suggestion[]>([]); // Update state type
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [isSelected, setIsSelected] = useState<Suggestion | null>(null); // Update state type

  useEffect(() => {
    if (!value || value.trim() === "") {
      setShowSuggestions(false);
      setFilteredSuggestions([]);
      setIsSelected(null);
    } else if (isSelected?.value !== value && suggestions && suggestions.length > 0) { // Update condition
      setShowSuggestions(true);
      const lowercasedValue = value.toLowerCase();
      const filtered = suggestions.filter((suggestion) =>
        suggestion.value.toLowerCase().includes(lowercasedValue) // Access 'value' field
      );
      setFilteredSuggestions(filtered);
    }
  }, [value, suggestions, isSelected]);

  const handleInputChange = (event: ChangeEvent<HTMLInputElement>) => {
    onChange(event.target.value);
  };

  const handleInputKeyUp: React.KeyboardEventHandler<HTMLInputElement> = debounce(
    (event) => {
      const inputValue = (event as React.ChangeEvent<HTMLInputElement>).target.value;

      if(inputValue?.length == 0 && onClearCallback) {
        onClearCallback();
        setShowSuggestions(false);
      }else if(inputValue && event.key == 'Enter' && onEnter) {
        onEnter();
        setShowSuggestions(false);
      } else if (inputValue && suggestions && suggestions.length > 0) {
        const lowercasedValue = inputValue.toLowerCase();
        const filtered = suggestions.filter((suggestion) =>
          suggestion.value.toLowerCase().includes(lowercasedValue)
        );
        setFilteredSuggestions(filtered);
        setShowSuggestions(true);
      }

      if (autoSuggestCallback) {
        autoSuggestCallback(inputValue);
      }
    },
    300
  );

  const handleSuggestionClick = (suggestion: Suggestion) => { // Update parameter type
    onChange(suggestion.value); // Update to use 'value' field

    if (onSuggestionSelect) {
      onSuggestionSelect(suggestion);
    }

    setIsSelected(suggestion);
    
    setShowSuggestions(false);
  };

  const handleInputBlur = () => {
    setTimeout(() => {
      setShowSuggestions(false);
    }, 100);
  };

  function debounce<T extends (...args: any[]) => void>(func: T, delay: number) {
    let timeoutId: NodeJS.Timeout;
    return function (this: ThisParameterType<T>, ...args: Parameters<T>) {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
      timeoutId = setTimeout(() => func.apply(this, args), delay);
    };
  }

  const highlightMatchingLetters = (text: string, searchValue: string) => {
    const escapedSearchValue = searchValue.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const regex = new RegExp(`(${escapedSearchValue})`, 'gi');
    return text.split(regex).map((part, index) =>
      regex.test(part) ? <strong key={index}>{part}</strong> : part
    );
  };  

  return (
    <div className="FounditUI_search-input-wrapper">
      <div className={`FounditUI_search-input-container FounditUI_${size}`}>
        {searchIcon ? (
          <div className="FounditUI_search-icon">{searchIcon}</div>
        ) : (
          <div className="FounditUI_search-text">Search:</div>
        )}
        <input
          type="text"
          placeholder={placeholder}
          value={value}
          onChange={handleInputChange}
          onKeyUp={handleInputKeyUp}
          onBlur={handleInputBlur}
          className="FounditUI_search-input"
        />
      </div>
      {showSuggestions && filteredSuggestions.length > 0 && (
        <div className="FounditUI_suggestion-container">
          <ul className="FounditUI_suggestion-list" onMouseDown={(e) => e.preventDefault()}>
            {filteredSuggestions.map((suggestion, index) => (
              <li key={index} onClick={() => handleSuggestionClick(suggestion)}>
                <div className='FounditUI_suggestion-item'>
                  {<div>{highlightMatchingLetters(suggestion.value, value)}</div>}
                  {extraInfo && <div className='FounditUI_suggestion--extra-info'>{suggestion.extraInfo}</div>}
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

export default SearchInput;
